import { inject, Injectable } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { AppSelectors } from '../../store/app/app.selectors';
import { catchError, filter, first, map, Observable, of, tap, timeout } from 'rxjs';
import {
  SetAppStateFromAlreadyVerifiedPhoneGuard,
  SetAppStateFromVerifyPhoneGuard,
  SetSessionExpired,
} from '../../store/app/app.actions';
import { ProgressBarService } from '../../_services/progress-bar-service/progress-bar.service';

@Injectable({ providedIn: 'root' })
class VerifyPhoneGuard {
  readonly store = inject(Store);
  readonly router = inject(Router);
  readonly progressBarService = inject(ProgressBarService);

  sendUserToOTP(): void {
    // Always default to hiding bar. Upon registration flow it is enabled from the creation password step
    this.progressBarService.setHideProgress(true);
    this.router.navigate(['sign-up/verify-phone']);
  }

  canActivate(): Observable<boolean> {
    return this.store.select(AppSelectors.isPhoneVerified).pipe(
      tap((phoneVerified) => {
        if (phoneVerified === undefined) {
          // If undefined, User details haven't been fetched. Fetch user details
          this.store.dispatch(new SetAppStateFromVerifyPhoneGuard());
        }
      }),
      filter((phoneVerified) => phoneVerified !== undefined),
      first(),
      timeout(8000), // 8 second timeout to prevent hanging on failed API calls
      catchError(() => {
        // If the API call times out or fails, trigger session expired flow
        this.store.dispatch(new SetSessionExpired(true));
        return of(false);
      }),
      map((phoneVerified) => {
        if (!phoneVerified) {
          this.sendUserToOTP();
        }
        return phoneVerified;
      }),
    );
  }
}

@Injectable({ providedIn: 'root' })
class AlreadyVerifiedPhoneGuard {
  readonly store = inject(Store);
  readonly router = inject(Router);

  canActivate(): Observable<boolean> {
    return this.store.select(AppSelectors.isPhoneVerified).pipe(
      tap((phoneVerified) => {
        if (phoneVerified === undefined) {
          // If undefined, User details haven't been fetched. Fetch user details
          this.store.dispatch(new SetAppStateFromAlreadyVerifiedPhoneGuard());
        }
      }),
      filter((phoneVerified) => phoneVerified !== undefined),
      first(),
      timeout(8000), // 8 second timeout to prevent hanging on failed API calls
      catchError(() => {
        // If the API call times out or fails, trigger session expired flow
        this.store.dispatch(new SetSessionExpired(true));
        return of(false);
      }),
      map((phoneVerified) => {
        if (phoneVerified) {
          this.router.navigate(['sign-up/subscription-selection']);
        }
        return !phoneVerified;
      }),
    );
  }
}

export const canActivatePhoneVerified: CanActivateFn = () => {
  return inject(VerifyPhoneGuard).canActivate();
};

export const canActivatePhoneAlreadyVerified: CanActivateFn = () => {
  return inject(AlreadyVerifiedPhoneGuard).canActivate();
};
